import { MouseEvent, SVGProps, useMemo } from 'react';

import { useTheme } from '@mui/material/styles';

import { mapPaletteColorToDataColorToken } from '../../../../utils/mapPaletteColorToDataColorToken';
import { getThemeColor } from '../../../../utils/theme';

export type BarSvgProps = Pick<
  SVGProps<SVGPathElement>,
  'stroke' | 'strokeOpacity' | 'strokeWidth' | 'fill' | 'fillOpacity' | 'style'
>;

export type BarShapeProps<T> = BarSvgProps & {
  index: number;
  payload?: T;
  background: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
  value: number | [number, number];
  testId?: string;
};

type BarShapeInternalProps<T> = {
  getCustomProps?: (point: T, index: number) => BarSvgProps;
  onClick?: (props: BarShapeProps<T>, event: MouseEvent) => void;
};

type Props<T> = BarShapeProps<T> & BarShapeInternalProps<T>;

/**
 * Determines if the bar should be rendered based on height and value.
 * Short bars (smaller than corner radius) must still render — clamp radius
 * instead of dropping them, otherwise sparse series vanish when the domain
 * is dominated by a taller series.
 */
const shouldRenderBar = ({
  height,
  value,
}: {
  height: number;
  value: number | [number, number];
}): boolean => {
  if (!height || height <= 0) {
    return false;
  }

  if (Array.isArray(value)) {
    return value[1] - value[0] !== 0;
  }

  return true;
};

/**
 * Creates the SVG path for the rounded bar shape
 */
const createBarPath = ({
  x,
  y,
  width,
  height,
  radius,
  isFirstStack,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
  isFirstStack: boolean;
}): string => {
  const halfWidth = width / 2;
  const radiusedWidth = halfWidth - radius;

  // Path segments for different bar types
  const noBackgroundFiller = `v${height - radius} h${-width} v${
    -height + radius
  }`;
  const backgroundFiller = `v${height} q0,${-radius} ${-radius},${-radius} h${
    -width + radius * 2
  } q${-radius},0 ${-radius},${radius} v${-height}`;

  const filler = isFirstStack ? noBackgroundFiller : backgroundFiller;

  return `M${
    x + halfWidth
  },${y} h${radiusedWidth} q${radius},0 ${radius},${radius} ${filler} q0,${-radius} ${radius},${-radius} z`;
};

export const BarShape = <T,>({
  onClick,
  getCustomProps,
  ...props
}: Props<T>) => {
  const {
    index,
    payload,
    fill,
    fillOpacity,
    stroke,
    strokeOpacity,
    strokeWidth = 1,
    style,
    x,
    y,
    width,
    height,
    radius = 4,
    value,
    testId,
  } = props;

  const theme = useTheme();

  const isFirstStack = !Array.isArray(value) || value[0] === 0;
  // Keep corner radius from exceeding bar dimensions so short bars still draw.
  const effectiveRadius = Math.min(
    radius,
    Math.max(0, height / 2),
    Math.max(0, width / 2)
  );

  const customProps = useMemo(
    () => (payload && getCustomProps?.(payload, index)) || {},
    [payload, index, getCustomProps]
  );

  const barPath = useMemo(
    () =>
      createBarPath({
        x,
        y,
        width,
        height,
        radius: effectiveRadius,
        isFirstStack,
      }),
    [x, y, width, height, effectiveRadius, isFirstStack]
  );

  if (!shouldRenderBar({ height, value })) {
    return null;
  }

  return (
    <g
      data-testid={`${testId}-${index}`}
      cursor={onClick ? 'pointer' : undefined}
      onClick={onClick ? (e) => onClick(props, e) : undefined}
      style={style}
    >
      <path
        x={x}
        y={y}
        width={width}
        height={height}
        fill={
          customProps.fill
            ? getThemeColor(
                theme,
                mapPaletteColorToDataColorToken(
                  customProps.fill,
                  customProps.fill
                )
              )
            : fill
        }
        fillOpacity={customProps.fillOpacity ?? fillOpacity}
        stroke={
          customProps.stroke
            ? getThemeColor(
                theme,
                mapPaletteColorToDataColorToken(
                  customProps.stroke,
                  customProps.stroke
                )
              )
            : stroke
        }
        strokeOpacity={customProps.strokeOpacity ?? strokeOpacity}
        strokeWidth={customProps.strokeWidth ?? strokeWidth}
        d={barPath}
      />
    </g>
  );
};
